import {useMutation} from '@tanstack/react-query';
import axios, {AxiosError} from 'axios';
import {compare, Operation} from 'fast-json-patch';
import {t} from 'i18next';
import React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {toast} from 'react-toastify';
import {useSlideErrors} from '../../../context/SlideErrorsContext';
import {type TypedTranslation} from '../../../i18n';
import {IValidationErrorResponse} from '../../../incase-domain/common/validationErrorResponse';
import {ISlide} from '../../../incase-domain/slide';
import {updateSelectedPresentationSlidesAction} from '../../../store/actions/presentation';
import {setIsSlideModifiedAction, setIsSlideModifiedDuringUpdateAction} from '../../../store/actions/slide';
import {selectedPresentationSelector} from '../../../store/selectors/presentation.selectors';
import {isSlideModifiedDuringUpdateSelector, selectedSlideSelector} from '../../../store/selectors/slide.selectors';
import CustomToastContainer from '../../../ui/components/common/CustomToastContainer';
import {swapArrayElement} from '../../utils';
import {handleValidationErrors, mapElementErrorsToIds} from '../../validation';
import {saveSlidePatch} from '../../../service/slides';
import {useSlidePreview} from './useSlidePreview';

export const UPDATE_SLIDE_KEY = ['updateSlide'];

interface useSaveSlideProps {
    setEmittedSlideModal?: (emittedSlideModal: boolean) => void;
    omitToastMessage?: boolean;
}

function useSaveSlide(options?: useSaveSlideProps): ReturnType<
    typeof useMutation<
        void,
        unknown,
        {
            slide: ISlide;
            isRejected?: boolean;
        },
        unknown
    >
> {
    const {setEmittedSlideModal, omitToastMessage} = options || {};
    const dispatch = useDispatch();
    const selectedPresentation = useSelector(selectedPresentationSelector);
    const currSlide = useSelector(selectedSlideSelector);
    const {validateSlide, setErrors, clearErrors} = useSlideErrors();

    const isSlideModifiedDuringUpdate = useSelector(isSlideModifiedDuringUpdateSelector);
    const {saveSlidePreview} = useSlidePreview();

    return useMutation(
        ({slide}) => {
            validateSlide(slide, true);

            const originalSlide = selectedPresentation.slides.find((item) => item._id === slide._id);
            if (!originalSlide) throw new Error();

            const patch: Operation[] = compare(originalSlide, slide);
            return saveSlidePatch(slide._id, patch);
        },
        {
            mutationKey: UPDATE_SLIDE_KEY,
            onSuccess: (_, {slide}: {slide: ISlide}) => {
                clearErrors();

                const selectedPresentationSlides = selectedPresentation.slides;

                const updatesSlideIndex = selectedPresentationSlides.findIndex((sl) => sl._id === slide._id);
                const updatedSlideList = swapArrayElement(selectedPresentationSlides, updatesSlideIndex, slide);

                dispatch(updateSelectedPresentationSlidesAction(updatedSlideList));

                saveSlidePreview();

                if (!isSlideModifiedDuringUpdate) {
                    dispatch(setIsSlideModifiedAction(false));
                }
                dispatch(setIsSlideModifiedDuringUpdateAction(false));
                if (!omitToastMessage) {
                    toast.success(t('toasts.successfullySaved'));
                }
            },
            onError: (error) => {
                if (axios.isAxiosError(error)) {
                    const errorData = (error as AxiosError<{message: string; error: string}>).response?.data;

                    if (error.response?.status === 400) {
                        let errorData = (error as AxiosError<{message: string; error: string}>).response
                            ?.data as unknown as IValidationErrorResponse;

                        errorData = mapElementErrorsToIds(errorData, currSlide);
                        setErrors(errorData);

                        toast.error(t(errorData?.locale as TypedTranslation));

                        if (errorData && errorData.errors) {
                            handleValidationErrors(errorData.errors);
                        }
                    }

                    if (errorData?.message.includes('slideChangeError')) {
                        const toastId = 'emittedSlideToastError';
                        if (!toast.isActive(toastId)) {
                            toast.error(
                                <CustomToastContainer
                                    message={t('editorPage.sharePresentations.sharePresentationModal.toastMessage.slideChangeError')}
                                    buttonAction={() => setEmittedSlideModal && setEmittedSlideModal(true)}
                                />,
                                {autoClose: 10000, toastId}
                            );
                            if (toast.isActive('emittedSlideToast')) {
                                toast.dismiss('emittedSlideToast');
                            }
                        }
                    }
                }
            },
        }
    );
}

export default useSaveSlide;
